import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import VideoGenerationStatus from './buttonApp/VideoGenerationStatus';
import ScheduledJobsManager from './buttonApp/ScheduledJobsManager';

const VideoGenerationStatusPage = () => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [buttonColor, setButtonColor] = useState('#3b82f6');
  const [error, setError] = useState(null);

  // Fetch all clients
  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.get('/auth/clients');
      
      if (response.data) {
        // Filter only clients (role 2) and sort by company name
        const clientList = response.data
          .filter(user => user.role === 2)
          .sort((a, b) => (a.company_name || '').localeCompare(b.company_name || ''));
        
        setClients(clientList);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      
      let errorMessage = 'Failed to load clients';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 404) {
        errorMessage = 'Clients endpoint not found';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to view clients';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error while fetching clients';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch client's button color when selected
  useEffect(() => {
    if (selectedClient) {
      fetchClientButtonColor(selectedClient._id);
    }
  }, [selectedClient]);

  const fetchClientButtonColor = async (userId) => {
    try {
      const response = await axios.get(`/api/menu-page/${userId}`);
      if (response.data && response.data.site_brand_color) {
        setButtonColor(response.data.site_brand_color);
      }
    } catch (error) {
      console.error('Error fetching button color:', error);
      setButtonColor('#3b82f6'); // Default color
    }
  };

  // Handle client selection
  const handleClientChange = (selectedOption) => {
    console.log('VideoGenerationStatusPage - Client selected:', selectedOption);
    if (selectedOption) {
      const client = clients.find(c => c._id === selectedOption.value);
      console.log('VideoGenerationStatusPage - Setting client:', client);
      setSelectedClient(client);
    } else {
      setSelectedClient(null);
    }
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedClient(null);
  };

  // Prepare options for react-select
  const clientOptions = clients.map(client => ({
    value: client._id,
    label: `${client.company_name || 'Unknown'} (${client.email})`,
  }));

  return (
    <div className="main-container">
      <div className="pd-ltr-20">
        {/* Page Header */}
        <div className="page-header" style={{ marginBottom: '30px' }}>
          <div className="row align-items-center">
            <div className="col-md-12">
              <div className="title">
                <h4 style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  color: '#2d3748',
                  fontWeight: 600
                }}>
                  <i className="bi bi-camera-video" style={{ color: buttonColor }}></i>
                  Video Generation Status Monitor
                  <span style={{
                    fontSize: '12px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontWeight: 600
                  }}>
                    ADMIN ONLY
                  </span>
                </h4>
              </div>
              <nav aria-label="breadcrumb" role="navigation">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <a href="/backend">Home</a>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Video Generation Status
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>

        {/* Client Selector Card */}
        <div className="card-box mb-30" style={{ 
          padding: '30px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          overflow: 'unset'
        }}>
          <div style={{ marginBottom: '20px' }}>
            <h5 style={{ 
              margin: '0 0 8px 0',
              color: '#2d3748',
              fontSize: '18px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <i className="bi bi-person-check" style={{ color: buttonColor }}></i>
              Select Client
            </h5>
            <p style={{ 
              margin: 0,
              color: '#718096',
              fontSize: '14px'
            }}>
              Choose a client to monitor their video generation status
            </p>
          </div>

          {/* Selected Client Display */}
          {selectedClient ? (
            <div style={{
              padding: '20px',
              backgroundColor: `${buttonColor}10`,
              border: `2px solid ${buttonColor}`,
              borderRadius: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${buttonColor}, ${buttonColor}dd)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}>
                  {(selectedClient.company_name || 'C').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: 'bold',
                    color: '#2d3748',
                    marginBottom: '4px'
                  }}>
                    {selectedClient.company_name || 'Unknown Client'}
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#718096',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <span>
                      <i className="bi bi-envelope" style={{ marginRight: '4px' }}></i>
                      {selectedClient.email}
                    </span>
                    <span>
                      <i className="bi bi-fingerprint" style={{ marginRight: '4px' }}></i>
                      {selectedClient._id}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={handleClearSelection}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'white',
                  border: `2px solid ${buttonColor}`,
                  borderRadius: '8px',
                  color: buttonColor,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = buttonColor;
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.color = buttonColor;
                }}
              >
                <i className="bi bi-x-circle"></i>
                Change Client
              </button>
            </div>
          ) : null}

          {/* React-Select Dropdown */}
          {isLoading ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#718096'
            }}>
              <i className="bi bi-hourglass-split" style={{ 
                fontSize: '48px', 
                marginBottom: '16px',
                animation: 'spin 2s linear infinite'
              }}></i>
              <p style={{ margin: 0 }}>Loading clients...</p>
            </div>
          ) : error ? (
            <div style={{
              background: 'linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%)',
              borderRadius: '12px',
              padding: '24px',
              border: '2px solid #ff6b6b',
              boxShadow: '0 4px 12px rgba(220, 53, 69, 0.15)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: '24px',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
                  animation: 'shake 0.5s ease-in-out'
                }}>
                  <i className="bi bi-exclamation-triangle"></i>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#dc2626',
                    marginBottom: '8px'
                  }}>
                    Failed to Load Clients
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: '#721c24',
                    lineHeight: '1.6',
                    marginBottom: '16px'
                  }}>
                    {error}
                  </div>
                  <button
                    onClick={fetchClients}
                    style={{
                      padding: '12px 24px',
                      background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(220, 53, 69, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    }}
                  >
                    <i className="bi bi-arrow-clockwise"></i>
                    <span>Try Again</span>
                  </button>
                </div>
              </div>
            </div>
          ) : clients.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#718096',
              backgroundColor: '#f8f9fa',
              borderRadius: '10px',
              border: '1px dashed #e2e8f0'
            }}>
              <i className="bi bi-inbox" style={{ 
                fontSize: '48px', 
                marginBottom: '16px',
                opacity: 0.3
              }}></i>
              <p style={{ margin: '0 0 8px 0', fontWeight: 500 }}>
                No clients available
              </p>
              <p style={{ margin: 0, fontSize: '13px' }}>
                Add clients to get started
              </p>
            </div>
          ) : !selectedClient ? (
            <div className="form-group mb-0">
              <label className="form-label" style={{
                fontWeight: 600,
                marginBottom: '12px',
                color: '#4a5568',
                fontSize: '14px'
              }}>
                <i className="bi bi-building" style={{ marginRight: '8px' }}></i>
                Client Selection
              </label>
              <Select
                options={clientOptions}
                onChange={handleClientChange}
                className="react-select-container"
                classNamePrefix="react-select"
                placeholder="Search and select a client..."
                isClearable
                isSearchable
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '48px',
                    borderRadius: '10px',
                    border: '2px solid #e2e8f0',
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: buttonColor,
                    },
                    '&:focus-within': {
                      borderColor: buttonColor,
                      boxShadow: `0 0 0 1px ${buttonColor}`,
                    }
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected 
                      ? buttonColor 
                      : state.isFocused 
                      ? `${buttonColor}20` 
                      : 'white',
                    color: state.isSelected ? 'white' : '#2d3748',
                    cursor: 'pointer',
                    padding: '12px 16px',
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: '#a0aec0',
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: '#2d3748',
                  })
                }}
              />
              <small className="form-text text-muted" style={{ 
                marginTop: '8px',
                display: 'block',
                fontSize: '13px'
              }}>
                <i className="bi bi-info-circle" style={{ marginRight: '4px' }}></i>
                Showing {clients.length} client{clients.length !== 1 ? 's' : ''}
              </small>
            </div>
          ) : null}
        </div>

        {/* Video Generation Status Component */}
        {selectedClient && (
          <>
            {/* Scheduled Jobs Section */}
            <div className="card-box mb-30" style={{ 
              padding: '30px',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}>
              <ScheduledJobsManager 
                userId={selectedClient._id} 
                buttonColor={buttonColor}
              />
            </div>

            {/* Video Generation Status */}
            <div className="card-box" style={{ 
              padding: '30px',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}>
              <VideoGenerationStatus 
                userId={selectedClient._id} 
                buttonColor={buttonColor}
              />
            </div>
          </>
        )}

        {/* Empty State when no client selected */}
        {!selectedClient && !isLoading && (
          <div className="card-box" style={{ 
            padding: '60px 30px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: `${buttonColor}10`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              border: `3px dashed ${buttonColor}40`
            }}>
              <i className="bi bi-camera-video" style={{ 
                fontSize: '48px', 
                color: buttonColor,
                opacity: 0.6
              }}></i>
            </div>
            <h4 style={{ 
              margin: '0 0 12px 0',
              color: '#2d3748',
              fontSize: '20px',
              fontWeight: 600
            }}>
              Select a Client to Begin
            </h4>
            <p style={{ 
              margin: 0,
              color: '#718096',
              fontSize: '15px',
              maxWidth: '500px',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}>
              Choose a client from the dropdown above to monitor their video generation status in real-time
            </p>
          </div>
        )}
      </div>

      <style jsx>{`
        @import url('https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.0/font/bootstrap-icons.css');
        
        ::-webkit-scrollbar {
          width: 6px;
        }
        
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 3px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes shake {
          0%, 100% {
            transform: translateX(0);
          }
          25% {
            transform: translateX(-10px);
          }
          75% {
            transform: translateX(10px);
          }
        }
      `}</style>
    </div>
  );
};

export default VideoGenerationStatusPage;
